import { faClose } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Modal, Select, Tag } from "antd";
import TextArea from "antd/es/input/TextArea";
import Spinner from "@src/components/spinner";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useCallback } from "react";
import { debounce } from "lodash";
import { CaretDownFilled } from "@ant-design/icons";
import { getAgencies } from "@src/util/query/agency.query";
import { getScheduledMobileEmployeesBySearchQuery } from "../../util/query/employee.query";
import { transferTaskMutation } from "@src/util/mutation/mobileServices.mutation";
import { useDispatch } from "react-redux";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";

export const TransferTask = ({ visible, onClose, data, refetch }) => {

    const [assignForm] = Form.useForm();
    const dispatch = useDispatch();
    console.log(data,"ddd");

    // ── Supplier search ──────────────────────────────────────────────────────
    const [getAgencyQuery, { data: agencyData, loading: agencyLoading }] = useLazyQuery(getAgencies);

    const handelSupplierSearch = async (input) => {
        await getAgencyQuery({
            fetchPolicy: 'cache-and-network',
            variables: {
                input: {
                    filters: {
                        advanceFilter: [[{
                            operator: "suppliername",
                            condition: "and",
                            filter: "contain_exactly",
                            filterValue: [input]
                        }]]
                    },
                    page: 1,
                    pageSize: 5,
                }
            }
        });
    };

    const debouncedSupplierSearch = useCallback(
        debounce((input) => {
            if (input?.length > 2) handelSupplierSearch(input);
        }, 500), []
    );

    // ── Employee search ──────────────────────────────────────────────────────
    const [getScheduledMobileEmployeesBySearch, { data: employeeData, loading: employeeDataLoading }] =
        useLazyQuery(getScheduledMobileEmployeesBySearchQuery);

    const handelEmployeeSearch = async (input) => {
        await getScheduledMobileEmployeesBySearch({
            variables: { search: input },
            fetchPolicy: 'cache-and-network'
        });
    };

    const debouncedEmployeeSearch = useCallback(
        debounce((input) => {
            if (input?.length > 0) handelEmployeeSearch(input);
        }, 500), []
    );

    // ── Mutation ─────────────────────────────────────────────────────────────
    const [transferTask, { loading: transferLoading }] = useMutation(transferTaskMutation);

    const handleSave = async () => {
        try {
            const values = await assignForm.validateFields();

            const isEmployee = values.custodianType === "employee";

            await transferTask({
                variables: {
                    input: {
                        taskId: data?._id,
                        dispatchMethod: values.custodianType,
                        employeeId: isEmployee ? values.custodianId : null,
                        supplierId: !isEmployee ? values.custodianId : null,
                        transferReason: values.transferReason,
                    }
                }
            });

            dispatch(setNotification({
                error: false,
                notificationState: true,
                message: "Task transferred successfully"
            }));

            assignForm.resetFields();
            onClose();
            refetch?.();

        } catch (err) {
            if (err?.message) {
                dispatch(setNotification({
                    error: true,
                    notificationState: true,
                    message: err.message
                }));
            }
        }
    };

    const handleClose = () => {
        assignForm.resetFields();
        onClose();
    };

    return (
        <Modal
            open={visible}
            width={560}
            closable={false}
            footer={
                <div className="drawer-footer" style={{ padding: '16px 40px', paddingBottom: '16px', display: 'flex', columnGap: '16px', marginTop: '-25px' }}>
                    <button
                        disabled={transferLoading}
                        className={transferLoading ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}
                        onClick={handleSave}
                    >
                        Transfer {transferLoading && <Spinner />}
                    </button>
                    <button
                        disabled={transferLoading}
                        className={transferLoading ? 'disabled-btn drawer-outlined-btn' : 'drawer-outlined-btn'}
                        onClick={handleClose}
                    >
                        Cancel
                    </button>
                </div>
            }
        >
            <div className='modal-header-title'>
                <span style={{ letterSpacing: '0.2px' }}>Transfer Task</span>
                <span onClick={handleClose}><FontAwesomeIcon className='close' icon={faClose} /></span>
            </div>

            <div className='modal-body' style={{ margin: '16px 0' }}>

                {/* Current task summary */}
                <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '12px 16px', marginBottom: 20, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div><strong>Site:</strong>&nbsp;{data?.siteDetail?.sitename || '—'}</div>
                    <div>
                        <strong>Currently Assigned To:</strong>&nbsp;
                        {data?.employeeDetail
                            ? `${data.employeeDetail.firstname ?? ''} ${data.employeeDetail.lastname ?? ''}`.trim()
                            : data?.supplierDetail?.suppliername || 'Unassigned'}
                    </div>
                    <div>
                        <strong>Dispatch Method:</strong>&nbsp;
                        {data?.dispatchMethod === 'employee' ? 'In-House Mobile Officer' : 'Supplier'}
                    </div>
                </div>

                <Form
                    initialValues={{ custodianType: data?.dispatchMethod || "supplier" }}
                    form={assignForm}
                    style={{ gap: '16px', display: 'flex', flexDirection: 'column' }}
                    requiredMark={false}
                    layout="vertical"
                    name="transferTaskForm"
                >
                    <Form.Item
                        name="custodianType"
                        label="Dispatch Method"
                        rules={[{ required: true, message: 'Please select a dispatch method' }]}
                    >
                        <Select
                            placeholder="Select dispatch method"
                            className='custom-select'
                            suffixIcon={<CaretDownFilled />}
                            onChange={() => assignForm.resetFields(["custodianId"])}
                        >
                            <Select.Option value="employee">In-House Mobile Officer</Select.Option>
                            <Select.Option value="supplier">Supplier</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        shouldUpdate={(prev, curr) => prev.custodianType !== curr.custodianType}
                    >
                        {({ getFieldValue }) => {
                            const method = getFieldValue("custodianType");
                            if (!method) return null;

                            const options = method === "employee"
                                ? employeeData?.getScheduledMobileEmployeesBySearch?.response
                                    ?.filter(emp => emp._id !== data?.employeeDetail?._id)
                                    ?.map(emp => (
                                    <Select.Option key={emp._id} value={emp._id}>
                                        {/* shift runner (on a mobile duty at an internal site today) or a direct-jobs driver */}
                                        <div style={{display:'flex', justifyContent:'space-between'}}>
                                            {emp.firstname + " " + (emp.lastname || "")}
                                            <Tag style={{height:'20px'}}>{emp.kind === "direct" ? "Direct jobs" : "On shift"}</Tag>
                                        </div>
                                    </Select.Option>
                                ))
                                : agencyData?.agencies?.agencies?.map(agency => (
                                    <Select.Option key={agency._id} value={agency._id}>
                                        {agency.suppliername}
                                    </Select.Option>
                                ));

                            return (
                                <Form.Item
                                    name="custodianId"
                                    label={method === "employee" ? "Mobile Officer" : "Supplier"}
                                    rules={[{ required: true, message: `Please select a ${method === "employee" ? "mobile officer" : "supplier"}` }]}
                                >
                                    <Select
                                        placeholder={method === "employee" ? "Search by name..." : "Search supplier..."}
                                        showSearch
                                        filterOption={false}
                                        className="custom-select"
                                        suffixIcon={<CaretDownFilled />}
                                        loading={employeeDataLoading || agencyLoading}
                                        onSearch={method === "employee" ? debouncedEmployeeSearch : debouncedSupplierSearch}
                                    >
                                        {employeeDataLoading || agencyLoading ? (
                                            <Select.Option disabled>
                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <Spinner />
                                                </div>
                                            </Select.Option>
                                        ) : options}
                                    </Select>
                                </Form.Item>
                            );
                        }}
                    </Form.Item>

                    <Form.Item
                        name="transferReason"
                        label="Transfer Reason"
                        rules={[{ required: true, message: 'Please provide a reason for the transfer' }]}
                    >
                        <TextArea rows={3} placeholder="Reason for transferring this task..." className='generic-input-control' />
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
}